import { useLazyQuery, useQuery } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { FinnishSSN } from 'finnish-ssn';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, Linking, Text, View } from 'react-native';
import WebView, { WebViewNavigation } from 'react-native-webview';
import styled from 'styled-components/native';

import { BaseText, FieldInput, Meta, Spinner, theme } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import MenuItem from 'components/common/MenuItem';
import { MarginView } from 'components/park/styles/styles';
import AccountContext from 'context/AccountContext';

import { READ_LEGAL_ADDRESS_STATUS } from 'graphql/queries/readLegalAddressStatus';
import { READ_VISMA_SIGN_DOCUMENT } from 'graphql/queries/readVismaSignDocument';
import { READ_VISMA_SIGN_METHODS } from 'graphql/queries/readVismaSignMethods';

import { useCityParkingStore } from 'stores/cityParkingStore';

import config from 'config/config';
import {
  ColumnWithMargins,
  StyledImage,
  StyledWrapper,
} from 'styles/ContainerStyles';
import { MainWrapper } from 'styles/ModalStackContainerStyles';
import {
  Query,
  QueryReadLegalAddressStatusArgs,
  QueryReadVismaSignDocumentArgs,
} from 'types/generatedSchemaTypes';
import { RootNavProps } from 'types/navigation';
import { isRunningLocally } from 'utils/commonUtils';
import { isWeb } from 'utils/deviceUtils';

const TERMS_URL =
  'https://www.turku.fi/liikenne-kadut-ja-kunnossapito/asukaspysakointilupa';
const LOCAL_TEST_SSN = '080772-131L';

const Container = styled(ColumnWithMargins)`
  flex: 1;
`;

const HeaderContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
`;

const BackButtonWrapper = styled.View`
  position: absolute;
  left: 20px;
`;

const WebContainer = styled(StyledWrapper)`
  background-color: ${(p) => p.theme.colors.white};
  flex: 1;
  border-radius: 20px;
`;

const CustomMainWrapper = styled(MainWrapper)`
  margin: 10px;
`;

const TermsLink = styled(BaseText)`
  text-decoration: underline;
  color: ${(p) => p.theme.colors.gray300};
  font-size: ${(p) => p.theme.textStyles.meta.fontSize};
  line-height: ${(p) => p.theme.textStyles.meta.lineHeight};
  letter-spacing: ${(p) => p.theme.textStyles.meta.letterSpacing};
`;

const CenteredWrapper = styled(StyledWrapper).attrs({
  center: true,
})`
  margin-bottom: 20px;
`;

interface AuthMethod {
  name: string;
  identifier: string;
  image: string;
  uid: string;
}

interface LegalAddressTermsAuthScreenProps {
  onClose?: () => void;
}

const useSSNValidation = () => {
  const [ssn, setSsn] = useState('');
  const [isValid, setIsValid] = useState(true);

  const validateSsn = useCallback((text: string) => {
    const trimmedSsn = text.trim().toUpperCase();
    setSsn(trimmedSsn);
    setIsValid(FinnishSSN.validate(trimmedSsn));
  }, []);

  return {
    ssn,
    isValid,
    validateSsn,
    ssnIsValid: isValid && ssn !== '',
  };
};

const useAuthMethods = (): AuthMethod[] => {
  const { data } = useQuery<Query>(READ_VISMA_SIGN_METHODS, {
    fetchPolicy: 'cache-and-network',
  });
  return useMemo(() => {
    const methods = data?.readVismaSignMethods ?? [];
    return methods.filter((method): method is AuthMethod => method !== null);
  }, [data]);
};

const useAuthentication = (onClose?: () => void) => {
  const [authUrl, setAuthUrl] = useState('');
  const [currentSsn, setCurrentSsn] = useState('');
  const { t } = useTranslation();
  const { refetchCustomer } = useContext(AccountContext);
  const navigation = useNavigation<RootNavProps>();

  const getRedirectionUrl = useCallback((ssn: string) => {
    const webRedirectUri = config.redirectUriWeb;
    const redirectUrl = `${webRedirectUri}/city-parking?ssn=${ssn}`;
    return redirectUrl;
  }, []);

  const [createVismaSignDocument, { loading }] = useLazyQuery<
    Query,
    QueryReadVismaSignDocumentArgs
  >(READ_VISMA_SIGN_DOCUMENT, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      setAuthUrl(data?.readVismaSignDocument?.fulfillUrl as string);
    },
    onError: () => {
      showToast(t('error.signDocumentFailed'), 'error');
    },
  });

  const [readLegalAddressStatus] = useLazyQuery<
    Query,
    QueryReadLegalAddressStatusArgs
  >(READ_LEGAL_ADDRESS_STATUS, {
    fetchPolicy: 'cache-and-network',
    onCompleted: () => {
      refetchCustomer();
      useCityParkingStore.getState().setStrongAuthCompleted(true);

      if (onClose) {
        onClose();
      } else if (!isWeb) {
        navigation.navigate('cityParking', {
          screen: 'cityPmcs',
          params: {
            ssn: isRunningLocally() ? LOCAL_TEST_SSN : currentSsn,
          },
        });
      }
    },
    onError: () => showToast(t('error.addressFetchFailed'), 'error'),
  });

  const startAuthentication = useCallback(
    (ssn: string, method: string) => {
      setCurrentSsn(ssn);
      createVismaSignDocument({
        variables: {
          input: {
            ssn,
            authService: method,
            returnUrl: isWeb
              ? getRedirectionUrl(ssn)
              : 'https://aimoapp.aimopark.io/success?includeAllIdProviders=true',
          },
        },
      });
    },
    [createVismaSignDocument, getRedirectionUrl]
  );

  const onNavigationStateChange = useCallback(
    async (event: WebViewNavigation) => {
      if (event.url.includes('includeAllIdProviders=true')) {
        readLegalAddressStatus({
          variables: {
            input: {
              ssn: isRunningLocally() ? LOCAL_TEST_SSN : currentSsn,
            },
          },
        });
      }
    },
    [readLegalAddressStatus, currentSsn]
  );

  return {
    authUrl,
    loading,
    startAuthentication,
    onNavigationStateChange,
  };
};

const SSNInput: React.FC<{
  ssn: string;
  isValid: boolean;
  loading: boolean;
  onChangeText: (text: string) => void;
}> = ({ ssn, isValid, loading, onChangeText }) => {
  const { t } = useTranslation();
  const maxWidth = isWeb ? 450 : 335;

  return (
    <CenteredWrapper maxWidth={maxWidth}>
      <FieldInput
        testID="strong-auth-ssn"
        value={ssn}
        disabled={loading}
        title={t('legalAddressAuth.ssn')}
        onChangeText={onChangeText}
      />
      <Meta
        color={
          !isValid && ssn !== '' ? theme.colors.boost.red : theme.colors.gray300
        }
        testID="strong-auth-meta">
        {!isValid && ssn.length > 0
          ? t('legalAddressAuth.invalidSsn')
          : t('legalAddressAuth.ssnInfo')}
      </Meta>
    </CenteredWrapper>
  );
};

const AuthMethodsList: React.FC<{
  methods: AuthMethod[];
  loading: boolean;
  ssnIsValid: boolean;
  ssn: string;
  onMethodPress: (ssn: string, method: string) => void;
}> = ({ methods, loading, ssnIsValid, ssn, onMethodPress }) => {
  if (loading || methods.length === 0) {
    return null;
  }

  const renderAuthMethod = ({ item }: { item: AuthMethod }) => (
    <MenuItem
      menuLabel={item.name}
      withArrow
      testID={`strong-auth-method-${item.name}`}
      onPress={() => ssnIsValid && onMethodPress(ssn, item.identifier)}
      arrowSize="s"
      imageComponent={
        <StyledImage
          source={{ uri: item.image }}
          resizeMode="contain"
          width={60}
          height={40}
        />
      }
    />
  );

  return (
    <FlatList
      data={methods}
      testID="strong-auth-method-list"
      renderItem={renderAuthMethod}
      keyExtractor={(item, index) => `${item?.uid}-${index}`}
      ListFooterComponent={MarginView}
    />
  );
};

const AuthWebView: React.FC<{
  authUrl: string;
  onNavigationStateChange: (event: WebViewNavigation) => void;
}> = ({ authUrl, onNavigationStateChange }) => {
  if (isWeb) {
    (window as any).open(authUrl, '_self');
    return null;
  }

  return (
    <WebView
      useWebKit
      testID="strong-auth-webview"
      width="100%"
      height="auto"
      source={{
        uri: authUrl,
        headers: {
          'User-Agent': 'AimoApp/1.0 (iOS; Mobile)',
          Accept:
            'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
          'Accept-Language': 'en-US,en;q=0.9',
          'Accept-Encoding': 'gzip, deflate',
          Connection: 'keep-alive',
          'Upgrade-Insecure-Requests': '1',
        },
      }}
      userAgent="AimoApp/1.0 (iOS; Mobile) AppleWebKit/605.1.15"
      onNavigationStateChange={onNavigationStateChange}
      onShouldStartLoadWithRequest={(request) => {
        if (request.url.includes('aimoapp.aimopark.io/success')) {
          setTimeout(() => {
            onNavigationStateChange({
              url: request.url,
            } as WebViewNavigation);
          }, 0);
          return false;
        }
        return true;
      }}
      renderError={(errorDomain, errorCode, errorDesc) => {
        const isDNSError =
          errorCode === -1003 ||
          errorCode === -2 ||
          errorDesc?.includes('ERR_NAME_NOT_RESOLVED');
        if (isDNSError) {
          return (
            <View
              style={{
                flex: 1,
                justifyContent: 'center',
                alignItems: 'center',
              }}>
              <Text>Authentication successful, completing...</Text>
            </View>
          );
        }

        return (
          <View
            style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
            <Text>Error loading page</Text>
            <Text>{errorDesc}</Text>
          </View>
        );
      }}
    />
  );
};

const AuthContent: React.FC<{
  methods: AuthMethod[];
  authUrl: string;
  loading: boolean;
  startAuth: (ssn: string, method: string) => void;
  onNavigationStateChange: (event: WebViewNavigation) => void;
}> = ({ methods, startAuth, authUrl, loading, onNavigationStateChange }) => {
  const { t } = useTranslation();
  const { ssn, isValid, validateSsn, ssnIsValid } = useSSNValidation();
  const maxWidth = isWeb ? 450 : 335;

  const openTerms = () => {
    Linking.openURL(TERMS_URL);
  };

  if (authUrl) {
    return (
      <AuthWebView
        authUrl={authUrl}
        onNavigationStateChange={onNavigationStateChange}
      />
    );
  }

  return (
    <Container mt={0} testID="legal-address-info-auth">
      <CustomMainWrapper>
        <CenteredWrapper maxWidth={maxWidth}>
          <BaseText testID="strong-auth-info" center>
            {t('legalAddressAuth.authInfo')}
          </BaseText>
        </CenteredWrapper>

        <CenteredWrapper maxWidth={maxWidth}>
          <BaseText testID="strong-auth-personal-info" center>
            {t('legalAddressAuth.identificationInfo')}
          </BaseText>
        </CenteredWrapper>

        <CenteredWrapper>
          <Meta
            testID="strong-auth-terms-text"
            center
            size="small"
            color={theme.colors.gray300}>
            {t('legalAddressAuth.termsPrefix')}{' '}
            <TermsLink onPress={openTerms}>
              {t('legalAddressAuth.terms')}
            </TermsLink>
            .
          </Meta>
        </CenteredWrapper>

        <SSNInput
          ssn={ssn}
          isValid={isValid}
          loading={loading}
          onChangeText={validateSsn}
        />

        {loading && (
          <CenteredWrapper>
            <Spinner />
          </CenteredWrapper>
        )}

        <AuthMethodsList
          methods={methods}
          loading={loading}
          ssnIsValid={ssnIsValid}
          ssn={ssn}
          onMethodPress={startAuth}
        />
      </CustomMainWrapper>
    </Container>
  );
};

const LegalAddressTermsAuthScreen: React.FC<
  LegalAddressTermsAuthScreenProps
> = ({ onClose }) => {
  const navigation = useNavigation<RootNavProps>();
  const authMethods = useAuthMethods();
  const { authUrl, loading, startAuthentication, onNavigationStateChange } =
    useAuthentication(onClose);

  const handleGoBack = () => {
    if (onClose) {
      onClose();
    } else {
      navigation.goBack();
    }
  };

  const authContentProps = {
    methods: authMethods,
    startAuth: startAuthentication,
    authUrl,
    loading,
    onNavigationStateChange,
    onGoBack: handleGoBack,
  };

  if (isWeb) {
    return (
      <WebContainer center maxWidth={600} testID="legal-address-info-auth-web">
        <AuthContent {...authContentProps} />
      </WebContainer>
    );
  }

  return (
    <CustomMainWrapper>
      <AuthContent {...authContentProps} />
    </CustomMainWrapper>
  );
};

export default LegalAddressTermsAuthScreen;
